import { Edit as EditIcon, ShoppingCart as OrderIcon } from "@mui/icons-material";
import { Button, Grid, LinearProgress, Stack, Typography } from "@mui/material";
import noop from "lodash/noop";
import type { FC } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../auth/AuthContext";
import { AssociatedFilesView } from "../components/associatedFiles/AssociatedFilesView";
import { InfoEdit } from "../components/editDialog/InfoEdit";
import NumberField from "../components/numberField/NumberField";
import ProductsTable from "../components/productsTable/ProductsTable";
import TagSelect from "../components/tagSelect/TagSelect";
import { Price } from "../misc/price";
import DownloadOrderPdfButton from "../orders/DownloadOrderPdfButton";
import { getExchangeRate } from "../orders/orderEdit/getExchangeRate";
import { useTripTagsByCategory } from "../orders/useTripTagsByCategory";
import BackToECommerceListButton from "./BackToECommerceListButton";
import BookOfferDialog from "./BookOfferDialog";
import type { ECommerceOfferResponse } from "./eCommerceApi";
import { useBookOffer } from "./useBookOffer";
import { useEcommerceOffer } from "./useECommerceOffer";

const EditButton: FC<{ offerId: string }> = ({ offerId }) => {
  const navigate = useNavigate();
  return (
    <Button color="primary" variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/orders/${offerId}`)}>
      Edytuj
    </Button>
  );
};

const BookOfferButton: FC<{ offer: Pick<ECommerceOfferResponse, "id" | "name" | "products"> }> = ({ offer }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  const { book } = useBookOffer(offer);

  const handleBook = async (note: string) => {
    await book(note, offer.products);
    navigate(`/orders/${offer.id.value}`);
  };

  return (
    <>
      <Button color="primary" variant="outlined" startIcon={<OrderIcon />} onClick={() => setDialogOpen(true)}>
        Zarezerwuj
      </Button>
      <BookOfferDialog
        offerName={offer.name}
        open={dialogOpen}
        onCancel={() => setDialogOpen(false)}
        onConfirm={handleBook}
      />
    </>
  );
};

const ECommerceOfferPage = () => {
  const { context } = useAuthContext();
  const { offerId, data: offer, isLoading: areOffersLoading } = useEcommerceOffer();
  const { tripTagsByCategory, isLoading: areTagsLoading } = useTripTagsByCategory();

  if (areOffersLoading || areTagsLoading) {
    return (
      <Grid container>
        <Grid size={12}>
          <LinearProgress data-testid="loading-indicator" />
        </Grid>
      </Grid>
    );
  }

  if (!offer) {
    return (
      <Grid
        container
        sx={{
          marginTop: 2,
          marginBottom: 2,
        }}
      >
        <Typography variant="h5">Taka oferta nie istnieje</Typography>
      </Grid>
    );
  }

  const role = context?.role;
  const isAdminOrAgent = role === "ADMIN" || role === "AGENT";
  const products = offer.products.map((p) => ({
    ...p,
    price: new Price(p.price ?? "UNAVAILABLE"),
    exchangeRate: getExchangeRate(p.currency, offer.currency, offer.currencyExchangeRate ?? undefined),
  }));
  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 2,
        paddingBottom: 2,
      }}
    >
      <Grid size={12}>
        <Typography variant="h6">Oferta {offer.id.userFriendly}</Typography>
      </Grid>
      <Grid size={12}>
        <Typography variant="h5">{offer.name}</Typography>
      </Grid>
      <Grid size={2}>
        <NumberField
          label="Liczba osób płatnych"
          fullWidth
          value={offer.participantsNumber.numberOfPaid ?? 0}
          readonly
          onChange={noop}
        />
      </Grid>
      <Grid size={2}>
        <NumberField
          label="Liczba osób obsługi"
          fullWidth
          value={offer.participantsNumber.numberOfStaff ?? 0}
          readonly
          onChange={noop}
        />
      </Grid>
      <Grid size={9}>
        <TagSelect tags={offer.tags} availableTags={tripTagsByCategory} maxVisibleTags={8} readonly onChange={noop} />
      </Grid>
      <Grid size={12}>
        <InfoEdit value={offer.info} readonly onChange={noop} />
      </Grid>
      <Grid size={12} data-testid="order-attachments">
        <AssociatedFilesView values={offer.files} groupByVisibility />
      </Grid>
      <Grid size={12}>
        <ProductsTable
          currency={offer.currency}
          totalPrice={new Price(offer.price ?? "UNAVAILABLE")}
          values={products}
        />
      </Grid>
      <Grid
        size={12}
        container
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <Stack direction="row" spacing={2}>
          <BackToECommerceListButton />
          <DownloadOrderPdfButton orderId={offer.id.value} filename={`${offer.name}.pdf`} />
          {isAdminOrAgent ? <EditButton offerId={offerId!} /> : <BookOfferButton offer={offer} />}
        </Stack>
      </Grid>
    </Grid>
  );
};

export default ECommerceOfferPage;
